/* eslint-env browser */
// Car types (wave 3, W2_Portal_Tables look): every vehicle type with its
// fares in the modern table, "Add vehicle type" at the top (admin), Edit and
// Cancellation slabs in each row, the picture and Delete in the row menu.
//
// Data: the car types loaded at sign-in (state.cartypes); nothing new is
// read. The writes are the same calls as before:
// - Delete: editCarType(row, 'Delete') (admin, with critical edits on).
// - Picture: editCarType(row, 'UpdateImage') with the chosen file on the row.
// - Cancellation slabs (and parcel types / options when optionsRequired):
//   the whole car type with the new list, dispatch(editCarType(row), "Update").
//   That call is kept exactly as it was: "Update" lands on dispatch, so
//   editCarType gets no method and writes the whole record with set(), the
//   same write as 'Update'.
// The store keeps the car types loaded at sign-in (EDIT_CAR_TYPE does not
// change it). As before, a new slab list or picture is put on the store's
// own car type object: Edit vehicle type writes the WHOLE record from that
// same object, so a copy here would let the next fare edit wipe the slab.
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import CancelScheduleSendOutlinedIcon from '@mui/icons-material/CancelScheduleSendOutlined';
import PhotoSizeSelectSmallOutlinedIcon from '@mui/icons-material/PhotoSizeSelectSmallOutlined';
import FitnessCenterOutlinedIcon from '@mui/icons-material/FitnessCenterOutlined';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import DirectionsCarFilledOutlinedIcon from '@mui/icons-material/DirectionsCarFilledOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { api } from 'common';
import { getLangKey } from 'common/src/other/getLangKey';
import { optionsRequired } from 'common/sharedFunctions';
import { showToast, confirmDialog, InlineMessage } from '../components/feedback';
import { DataTable, RecordDialog, PageHeader, calmCaps, usePageState } from '../components/modern';
import { tokens as c } from '../theme';

// Car types deleted in this session: the store keeps them (EDIT_CAR_TYPE
// does not change it), so they are hidden here while the store still holds
// the same sign-in copy.
let removedCache = { source: null, ids: [] };
const removedFor = (source) => (removedCache.source === source ? removedCache.ids : []);

// A nested list (cancelSlab, parcelTypes, options) as stored on a car type.
const subListOf = (row, kind) => {
  const raw = row ? row[kind] : null;
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (raw && typeof raw === 'object') return Object.values(raw).filter(Boolean);
  return [];
};

const SUB_KINDS = {
  cancelSlab: { titleKey: 'cancelSlab', title: 'Cancellation slabs', icon: <CancelScheduleSendOutlinedIcon /> },
  parcelTypes: { titleKey: 'parcel_types', title: 'Parcel types', icon: <PhotoSizeSelectSmallOutlinedIcon /> },
  options: { titleKey: 'options', title: 'Options', icon: <FitnessCenterOutlinedIcon /> },
};

export default function CarTypes() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { editCarType, convertLanguage } = api;
  const settings = useSelector((s) => s.settingsdata.settings);
  const cartypes = useSelector((s) => s.cartypes);
  const auth = useSelector((s) => s.auth);
  const isAdmin = auth.profile?.usertype === 'admin';
  const critical = !!settings.AllowCriticalEditsAdmin;
  const langLocale = auth?.profile?.lang?.langLocale ? auth.profile.lang.langLocale : null;

  // The page and the table's search are kept while staff open a record and
  // come back (owner, 28 Oct); components/modern/pageState.js.
  const [page, setPage] = usePageState('car_types', 'page', Number.isInteger(state?.pageNo) && state.pageNo >= 0 ? state.pageNo : 0);
  useEffect(() => {
    if (Number.isInteger(state?.pageNo) && state.pageNo >= 0) setPage(state.pageNo);
  }, [state]);

  // Bumped after a write that changed one of the store's car type objects
  // in place, so the rows draw again.
  const [version, setVersion] = useState(0);
  const source = cartypes.cars;
  const [removed, setRemoved] = useState(() => removedFor(source));
  useEffect(() => {
    setRemoved(removedFor(source));
  }, [source]);
  const data = useMemo(
    () => (Array.isArray(source) ? source.filter((row) => row && !removed.includes(row.id)) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [source, removed, version]
  );

  const shownName = (row) => (row && row.name ? t(getLangKey(row.name), { defaultValue: row.name }) : '');
  const shownDescription = (item) => (item && item.description ? t(getLangKey(item.description), { defaultValue: item.description }) : '');
  const navState = { state: { pageNo: page } };
  const goAdd = () => navigate('/cartypes/editcartype', navState);
  const goEdit = (row) => navigate(`/cartypes/editcartype/${row.id}`, navState);

  // ── Delete a car type (admin) ─────────────────────────────────────────
  const handleDelete = async (row) => {
    if (!critical) {
      showToast.warning(t('demo_mode'));
      return;
    }
    const ok = await confirmDialog({
      title: t('car_types_delete_title', { name: shownName(row), defaultValue: 'Delete the vehicle type “{{name}}”?' }),
      body: t('car_types_delete_body', { defaultValue: 'Customers and drivers will no longer see it in the apps. This cannot be undone.' }),
      confirmLabel: t('delete', { defaultValue: 'Delete' }),
      cancelLabel: t('confirm_cancel', { defaultValue: 'Cancel' }),
      destructive: true,
    });
    if (!ok) return;
    try {
      await dispatch(editCarType(row, 'Delete'));
    } catch (e) {
      showToast.error(e && e.message ? e.message : t('form_save_failed', { defaultValue: 'Could not save' }));
      return;
    }
    const ids = removedFor(source).concat([row.id]);
    removedCache = { source, ids };
    setRemoved(ids);
    showToast.success(t('car_types_deleted', { defaultValue: 'Vehicle type deleted' }));
  };

  // ── Picture (admin) ───────────────────────────────────────────────────
  const [imageFor, setImageFor] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imageError, setImageError] = useState(null);
  const [imageBusy, setImageBusy] = useState(false);
  const previewUrl = useMemo(() => (imageFile ? URL.createObjectURL(imageFile) : null), [imageFile]);
  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl]
  );
  const openImage = (row) => {
    if (!isAdmin) return;
    setImageFor(row);
    setImageFile(null);
    setImageError(null);
  };
  const closeImage = () => {
    if (imageBusy) return;
    setImageFor(null);
    setImageFile(null);
    setImageError(null);
  };
  const saveImage = async () => {
    if (!critical) {
      showToast.warning(t('demo_mode'));
      return;
    }
    if (!imageFile) {
      setImageError(t('choose_image_first'));
      return;
    }
    const row = imageFor;
    const before = row.image;
    setImageBusy(true);
    setImageError(null);
    // The same call as before: the file goes on the car type itself and
    // editCarType uploads it, then saves the record with the picture's link.
    row.image = imageFile;
    try {
      await dispatch(editCarType(row, 'UpdateImage'));
    } catch (e) {
      row.image = before;
      setImageBusy(false);
      setImageError(e && e.message ? e.message : t('form_save_failed', { defaultValue: 'Could not save' }));
      return;
    }
    setImageBusy(false);
    setImageFor(null);
    setImageFile(null);
    setVersion((v) => v + 1);
    showToast.success(t('car_types_image_saved', { defaultValue: 'Picture saved' }));
  };

  // ── Nested lists: cancellation slabs (parcel types, options) ──────────
  const [subFor, setSubFor] = useState(null); // { row, kind }
  const [subEditing, setSubEditing] = useState(null); // {} = add, { item } = edit
  const subKind = subFor ? subFor.kind : 'cancelSlab';
  const subMeta = SUB_KINDS[subKind];
  const subRows = subFor ? subListOf(subFor.row, subFor.kind) : [];
  const subTitle = calmCaps(t(subMeta.titleKey, { defaultValue: subMeta.title }));

  const openSub = (row, kind) => {
    setSubFor({ row, kind });
    setSubEditing(null);
  };
  const guardCritical = () => {
    if (critical) return true;
    showToast.warning(t('demo_mode'));
    return false;
  };
  const writeSub = (row, kind, next) => {
    row[kind] = next;
    // Kept exactly as before: "Update" is the second argument of dispatch,
    // so editCarType runs without a method and set()s the whole car type.
    dispatch(editCarType(row), 'Update');
    setVersion((v) => v + 1);
  };

  const saveSub = async (values, { mode, initialValues, changed }) => {
    if (!guardCritical()) return;
    const { row, kind } = subFor;
    const current = subListOf(row, kind);
    if (mode === 'add') {
      if (values.description && kind !== 'cancelSlab') await convertLanguage(values.description, langLocale);
      writeSub(row, kind, current.concat([values]));
      showToast.success(t('car_types_sub_added', { defaultValue: 'Added' }));
      return;
    }
    const index = current.indexOf(initialValues);
    if (index < 0) throw new Error(t('car_types_list_changed', { defaultValue: 'The list changed. Close this and try again.' }));
    if (!changed.length) return;
    // Only the fields that were edited change; the others keep their
    // stored value and type.
    const item = { ...initialValues };
    changed.forEach((name) => {
      item[name] = values[name];
    });
    if (initialValues.description !== item.description && kind !== 'cancelSlab') await convertLanguage(item.description, langLocale);
    const next = current.slice();
    next[index] = item;
    writeSub(row, kind, next);
    showToast.success(t('saved', { defaultValue: 'Saved' }));
  };

  // Removes the chosen line only. (The old filter also removed every other
  // line with the same amount or the same minutes / description.)
  const deleteSub = async (item) => {
    if (!guardCritical()) return;
    const { row, kind } = subFor;
    const label = kind === 'cancelSlab' ? `${item.minsDelayed}` : shownDescription(item);
    const ok = await confirmDialog({
      title: t('car_types_sub_delete_title', { item: label, defaultValue: 'Delete “{{item}}”?' }),
      body: t('car_types_sub_delete_body', { defaultValue: 'It is removed from this vehicle type at once.' }),
      confirmLabel: t('delete', { defaultValue: 'Delete' }),
      cancelLabel: t('confirm_cancel', { defaultValue: 'Cancel' }),
      destructive: true,
    });
    if (!ok) return;
    const current = subListOf(row, kind);
    if (current.indexOf(item) < 0) {
      showToast.error(t('car_types_list_changed', { defaultValue: 'The list changed. Close this and try again.' }));
      return;
    }
    writeSub(
      row,
      kind,
      current.filter((x) => x !== item)
    );
    showToast.success(t('car_types_sub_deleted', { defaultValue: 'Deleted' }));
  };

  const amountColumn = { title: t('amount'), field: 'amount', type: 'money', sortable: false };
  const subColumns =
    subKind === 'cancelSlab'
      ? [{ title: t('minsDelayed'), field: 'minsDelayed', type: 'number', sortable: false }, amountColumn]
      : [
          {
            title: t('description'),
            field: 'description',
            sortable: false,
            searchValue: (item) => `${shownDescription(item)} ${item.description || ''}`,
            exportValue: (item) => shownDescription(item),
            render: (item) => shownDescription(item),
          },
          amountColumn,
        ];
  const numberCheck = (v) => {
    const s = String(v === null || v === undefined ? '' : v).trim();
    if (!s) return null;
    const n = Number(s);
    return Number.isFinite(n) && n >= 0 ? null : t('form_number_invalid', { defaultValue: 'Enter a number' });
  };
  const subFields =
    subKind === 'cancelSlab'
      ? [
          // Stored as typed (text), as before; the trip trigger reads it with parseFloat.
          { name: 'minsDelayed', label: t('minsDelayed'), required: true, autoFocus: true, maxLength: 6, width: 'half', validate: numberCheck },
          { name: 'amount', label: t('amount'), type: 'money', required: true, min: 0, width: 'half', adornment: settings.symbol },
        ]
      : [
          { name: 'description', label: t('description'), required: true, autoFocus: true, maxLength: 200 },
          { name: 'amount', label: t('amount'), type: 'money', required: true, min: 0, adornment: settings.symbol },
        ];
  const subAddLabel = t('car_types_sub_add', { defaultValue: 'Add line' });
  const subIsEdit = !!(subEditing && subEditing.item);

  // ── The car types table ───────────────────────────────────────────────
  const feeTypes = { flat: t('flat'), percentage: t('percentage') };
  const columns = [
    {
      title: t('name'),
      field: 'name',
      searchValue: (row) => `${shownName(row)} ${row.name || ''}`,
      sortValue: (row) => shownName(row),
      exportValue: (row) => shownName(row),
      nowrap: true,
      render: (row) => (
        <Box component="span" sx={{ fontWeight: 600 }}>
          {shownName(row)}
        </Box>
      ),
    },
    {
      title: t('image'),
      field: 'image',
      sortable: false,
      searchable: false,
      print: false,
      render: (row) => {
        if (!row.image || typeof row.image !== 'string') return null;
        const img = (
          <Box
            component="img"
            alt={isAdmin ? '' : t('car_image')}
            src={row.image}
            sx={{ width: 56, height: 40, objectFit: 'contain', display: 'block', borderRadius: '8px', bgcolor: c.SURFACE_ALT }}
          />
        );
        return isAdmin ? (
          <ButtonBase
            onClick={() => openImage(row)}
            aria-label={`${t('upload_car_image')}: ${shownName(row)}`}
            sx={{ borderRadius: '8px', '&.Mui-focusVisible': { outline: `2px solid ${c.ACCENT}`, outlineOffset: 2 } }}
          >
            {img}
          </ButtonBase>
        ) : (
          img
        );
      },
    },
    { title: t('base_fare'), field: 'base_fare', type: 'money' },
    { title: t('rate_per_unit_distance'), field: 'rate_per_unit_distance', type: 'money' },
    { title: t('rate_per_hour'), field: 'rate_per_hour', type: 'money' },
    { title: t('min_fare'), field: 'min_fare', type: 'money' },
    { title: t('convenience_fee'), field: 'convenience_fees', type: 'number' },
    { title: t('convenience_fee_type'), field: 'convenience_fee_type', lookup: feeTypes },
    { title: t('base_fare_meter'), field: 'base_fare_meter', type: 'money' },
    { title: t('rate_per_unit_distance_meter'), field: 'rate_per_unit_distance_meter', type: 'money' },
    { title: t('rate_per_hour_meter'), field: 'rate_per_hour_meter', type: 'money' },
    { title: t('min_fare_meter'), field: 'min_fare_meter', type: 'money' },
    { title: t('convenience_fee_meter'), field: 'convenience_fees_meter', type: 'number' },
    { title: t('convenience_fee_type_meter'), field: 'convenience_fee_type_meter', lookup: feeTypes },
    { title: t('rate_per_minute_wait_meter'), field: 'rate_per_minute_wait_meter', type: 'money' },
    { title: t('fleet_admin_comission'), field: 'fleet_admin_fee', type: 'number' },
    { title: t('daily_flat_rate', { defaultValue: 'Daily flat rate (incl. VAT)' }), field: 'daily_flat_rate', type: 'money' },
    { title: t('extra_info'), field: 'extra_info', minWidth: 160 },
    { title: t('position'), field: 'pos', type: 'number', defaultSort: 'asc' },
  ];

  const subAction = (kind) => ({
    key: kind,
    label: calmCaps(t(SUB_KINDS[kind].titleKey, { defaultValue: SUB_KINDS[kind].title })),
    icon: SUB_KINDS[kind].icon,
    inline: kind === 'cancelSlab',
    onClick: (row) => openSub(row, kind),
  });
  const actions = [
    ...(isAdmin ? [{ key: 'edit', label: t('edit'), icon: <EditOutlinedIcon />, inline: true, onClick: goEdit }] : []),
    subAction('cancelSlab'),
    ...(optionsRequired ? [subAction('parcelTypes'), subAction('options')] : []),
    ...(isAdmin ? [{ key: 'image', label: t('upload_car_image'), icon: <AddPhotoAlternateOutlinedIcon />, onClick: openImage }] : []),
    ...(isAdmin
      ? [{ key: 'delete', label: t('car_types_delete', { defaultValue: 'Delete vehicle type' }), icon: <DeleteOutlineRoundedIcon />, destructive: true, onClick: handleDelete }]
      : []),
  ];

  const title = calmCaps(t('car_type_title'));
  const addLabel = t('add_carType');

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PageHeader primaryAction={{ label: addLabel, icon: <AddRoundedIcon />, onClick: goAdd, show: isAdmin }} />
      <DataTable
        title={title}
        rows={data}
        columns={columns}
        rowKey="id"
        loading={!!cartypes.loading && !data.length}
        countText={({ shown, total, query, loading }) =>
          loading
            ? t('loading', { defaultValue: 'Loading…' })
            : query
              ? t('table_count_shown', { shown, total, defaultValue: '{{shown}} of {{total}}' })
              : t('car_types_count', {
                  count: total,
                  defaultValue_one: '{{count}} vehicle type',
                  defaultValue_other: '{{count}} vehicle types',
                  defaultValue: '{{count}} vehicle types',
                })
        }
        rowLabel={shownName}
        actions={actions}
        searchPlaceholder={t('car_types_search', { defaultValue: 'Search vehicle types' })}
        page={page}
        onPageChange={setPage}
        resortKey={version}
        prefsKey="car_types"
        stateKey="car_types"
        exportCsv
        exportPrint
        exportFileName="vehicle_types"
        exportTitle={title}
        minWidth={1900}
        emptyState={({ query }) =>
          query
            ? null
            : {
                icon: <DirectionsCarFilledOutlinedIcon />,
                title: t('car_types_empty', { defaultValue: 'No vehicle types yet' }),
                body: t('car_types_empty_body', { defaultValue: 'Add a vehicle type to set the fares customers pay.' }),
                actionLabel: isAdmin ? addLabel : undefined,
                onAction: isAdmin ? goAdd : undefined,
              }
        }
      />

      {/* Picture */}
      <Dialog open={!!imageFor} onClose={closeImage} fullWidth maxWidth="xs" fullScreen={phone} aria-labelledby="cartype-image-title">
        <DialogTitle id="cartype-image-title" sx={{ pr: 8 }}>
          {t('upload_car_image')}
          {imageFor ? (
            <Typography component="span" sx={{ display: 'block', fontSize: 14, color: c.TEXT_3, fontWeight: 400 }}>
              {shownName(imageFor)}
            </Typography>
          ) : null}
          <IconButton
            aria-label={t('close_dialog', { defaultValue: 'Close' })}
            onClick={closeImage}
            disabled={imageBusy}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {imageError ? (
            <InlineMessage kind="error" sx={{ mb: 2 }}>
              {imageError}
            </InlineMessage>
          ) : null}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, pt: 0.5 }}>
            {previewUrl || (imageFor && typeof imageFor.image === 'string' && imageFor.image) ? (
              <Box
                component="img"
                alt={t('car_image')}
                src={previewUrl || imageFor.image}
                sx={{ width: 200, height: 200, objectFit: 'contain', borderRadius: '12px', border: `1px solid ${c.OUTLINE}`, bgcolor: c.SURFACE_ALT }}
              />
            ) : null}
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center', gap: 1 }}>
              <Button variant="outlined" color="neutral" component="label" startIcon={<UploadFileOutlinedIcon />} disabled={imageBusy}>
                {imageFile
                  ? t('car_types_image_other', { defaultValue: 'Choose another picture' })
                  : t('car_types_image_choose', { defaultValue: 'Choose a picture' })}
                <input
                  hidden
                  type="file"
                  accept="image/*"
                  name={t('image')}
                  onChange={(event) => {
                    const file = event.target.files && event.target.files[0];
                    if (file) {
                      setImageFile(file);
                      setImageError(null);
                    }
                    event.target.value = '';
                  }}
                />
              </Button>
              {imageFile ? (
                <Button variant="text" color="neutral" onClick={() => setImageFile(null)} disabled={imageBusy}>
                  {t('car_types_image_remove', { defaultValue: 'Remove' })}
                </Button>
              ) : null}
            </Box>
            {imageFile ? (
              <Typography sx={{ fontSize: 13.5, color: c.TEXT_3, overflowWrap: 'anywhere', textAlign: 'center' }}>{imageFile.name}</Typography>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="text" color="neutral" onClick={closeImage} disabled={imageBusy}>
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={saveImage}
            disabled={imageBusy}
            startIcon={imageBusy ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {imageBusy ? t('form_saving', { defaultValue: 'Saving…' }) : t('save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancellation slabs (parcel types, options) of one vehicle type */}
      <Dialog open={!!subFor} onClose={() => setSubFor(null)} fullWidth maxWidth="md" fullScreen={phone} aria-labelledby="cartype-sub-title">
        <DialogTitle id="cartype-sub-title" sx={{ pr: 8 }}>
          {subTitle}
          {subFor ? (
            <Typography component="span" sx={{ display: 'block', fontSize: 14, color: c.TEXT_3, fontWeight: 400 }}>
              {shownName(subFor.row)}
            </Typography>
          ) : null}
          <IconButton
            aria-label={t('close_dialog', { defaultValue: 'Close' })}
            onClick={() => setSubFor(null)}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <PageHeader
            primaryAction={{
              label: subAddLabel,
              icon: <AddRoundedIcon />,
              onClick: () => {
                if (guardCritical()) setSubEditing({});
              },
            }}
          />
          <DataTable
            title={subTitle}
            rows={subRows}
            columns={subColumns}
            rowKey={(item, index) => `${index}-${item.minsDelayed || item.description || ''}-${item.amount}`}
            countText={({ total }) =>
              t('car_types_sub_count', { count: total, defaultValue_one: '{{count}} line', defaultValue_other: '{{count}} lines', defaultValue: '{{count}} lines' })
            }
            search={false}
            paging={false}
            densityToggle={false}
            columnsMenu={false}
            maxHeight={null}
            minWidth={360}
            resortKey={version}
            rowLabel={(item) => (subKind === 'cancelSlab' ? `${item.minsDelayed}` : shownDescription(item))}
            actions={[
              {
                key: 'edit',
                label: t('edit'),
                icon: <EditOutlinedIcon />,
                inline: true,
                onClick: (item) => {
                  if (guardCritical()) setSubEditing({ item });
                },
              },
              {
                key: 'delete',
                label: t('delete', { defaultValue: 'Delete' }),
                icon: <DeleteOutlineRoundedIcon />,
                inline: true,
                destructive: true,
                onClick: deleteSub,
              },
            ]}
            exportCsv
            exportPrint
            exportFileName={`${subKind}_${subFor && subFor.row && subFor.row.name ? String(subFor.row.name).replace(/\s+/g, '_') : 'vehicle_type'}`}
            exportTitle={subFor ? `${subTitle} · ${shownName(subFor.row)}` : subTitle}
            emptyState={{
              icon: subMeta.icon,
              title: t('car_types_sub_empty', { defaultValue: 'Nothing added yet' }),
              actionLabel: subAddLabel,
              onAction: () => {
                if (guardCritical()) setSubEditing({});
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="neutral" onClick={() => setSubFor(null)}>
            {t('close_dialog', { defaultValue: 'Close' })}
          </Button>
        </DialogActions>
      </Dialog>

      <RecordDialog
        open={!!subEditing}
        mode={subIsEdit ? 'edit' : 'add'}
        title={subIsEdit ? `${t('edit')} · ${subTitle}` : `${subAddLabel} · ${subTitle}`}
        fields={subFields}
        initialValues={subIsEdit ? subEditing.item : undefined}
        onSubmit={saveSub}
        onClose={() => setSubEditing(null)}
        maxWidth="xs"
      />
    </Box>
  );
}
